import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  PoI18nConfig,
  PoI18nModule,
  PoLoadingModule,
  PoMenuModule,
  PoNotificationModule,
  PoToolbarModule,
} from '@po-ui/ng-components';
import { AuthModuleOptions, AuthModule as FluigAuthModule } from '@totvs-apps-components/auth';
import { OAuthStorage } from 'angular-oauth2-oidc';
import {
  AtsSystemEnum,
  AtsTooltipBadgeComponent,
  AtsUiLibModule,
  ERROR_HANDLER_CONFIG,
  ErrorHandlerConfig,
  HttpStrategyConfig,
  LanguageKey,
  MENU_FILTER,
  METRICS_CONFIG,
  MetricsConfig,
  UnauthorizedErrorHandler,
} from 'ats-ui-lib';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { AuthCallbackComponent } from './pages/auth-callback/auth-callback.component';
import { AuthSocialLoginCallbackComponent } from './pages/auth-social-login-callback/auth-social-login-callback.component';
import { BehavioralAssessmentAnswerRedirectComponent } from './pages/behavioral-assessment-answer-redirect/behavioral-assessment-answer-redirect.component';
import { MainComponent } from './pages/main/main.component';
import { OAuthLoginComponent } from './pages/oauth-login/oauth-login.component';
import { ImportTranslationLoader } from './root/language/import-translation-loader';
import { MenuFilterService } from './root/menu/menu-filter.service';
import { AtsUnauthorizedErrorHandler } from './root/unauthorized-error-handler/unauthorized-error-handler';
import { SharedModule } from './shared/shared.module';
import { LocaleUtil } from './shared/utils/locale-util';

const fluigAuthOptions: AuthModuleOptions = {
  config: {
    shouldPromptTokenRequest: environment.fluigOptions.shouldPromptTokenRequest,
    appCodeDevMode: environment.fluigOptions.appCodeDevMode,
    ...environment.fluigOptions.env,
    pkceEnabled: false,
    blacklistedRoutes: [/cities/i, /state/i, /states/i],
  },
};

export function storageFactory(): OAuthStorage {
  return localStorage;
}

LocaleUtil.registerAppLocales();

const i18nConfig: PoI18nConfig = {
  contexts: {
    general: {},
  },
  default: {
    language: 'pt-BR',
    context: 'general',
  },
};

const httpStrategyConfig = Object.freeze(
  new HttpStrategyConfig({ urlApi: new URL(environment.applicantApiEndpoint) }),
);

const errorHandlerConfig: ErrorHandlerConfig = {
  blacklistDomainsFromToasterGeneration: [/\w+\.fluigidentity\.net/, /\w+\.fluigidentity\.com/],
};

const metricsConfig: MetricsConfig = {
  appAccessMetric: {
    sendEveryHours: environment.metrics.appAccessMetric.sendEveryHours,
    systemIdentifier: AtsSystemEnum.Candidate,
  },
  metricsApiEndpoint: environment.metricsApiEndpoint,
  errorMetric: {
    enabled: environment.metrics.errorMetric.enabled,
  },
};

@NgModule({
  declarations: [
    AppComponent,
    AuthCallbackComponent,
    MainComponent,
    AuthSocialLoginCallbackComponent,
    OAuthLoginComponent,
    BehavioralAssessmentAnswerRedirectComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    PoToolbarModule,
    PoMenuModule,
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useClass: ImportTranslationLoader },
    }),
    PoI18nModule.config(i18nConfig),
    SharedModule,
    BrowserAnimationsModule,
    PoNotificationModule,
    CoreModule,
    AtsUiLibModule.forRoot({
      fixedLanguage: LanguageKey.Portuguese,
    }),
    FormsModule,
    PoLoadingModule,
    FluigAuthModule.forRoot(fluigAuthOptions),
    AtsTooltipBadgeComponent,
  ],
  providers: [
    { provide: Window, useValue: window },
    { provide: 'HTTP_STRATEGY_CONFIG', useValue: httpStrategyConfig },
    {
      provide: ERROR_HANDLER_CONFIG,
      useValue: errorHandlerConfig,
    },
    { provide: OAuthStorage, useFactory: storageFactory },
    { provide: UnauthorizedErrorHandler, useClass: AtsUnauthorizedErrorHandler },
    { provide: METRICS_CONFIG, useValue: metricsConfig },
    { provide: MENU_FILTER, useClass: MenuFilterService },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
